import React, { useState } from "react";
import {
  Check,
  ChevronRight,
  Circle,
  ListChecks,
  Loader2,
  Minimize2,
  Sparkles,
  X,
} from "lucide-react";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/lib/ui/Collapsible";
import { renderMarkdown } from "@src/features/chat/renderTextWithInlineCitations";
import { OpusAgentChatTurnStatus } from "@src/types.generated";
import {
  getEventKind,
  planItemStatus,
  resolveToolCallStatus,
  toolRowLabel,
} from "./events";
import { OpusAgentErrorBubble } from "./OpusAgentErrorBubble";
import type { OpusAgentEventFragment } from "./useOpusAgentChat.generated";

interface OpusAgentEventBlockProps {
  event: OpusAgentEventFragment;
  turnStatus: OpusAgentChatTurnStatus;
  // Offered on an error event's bubble when this failure is the turn's final
  // state (the parent passes it only for the last error event of a FAILED
  // turn — one retry affordance per turn, and never on a turn that went on
  // to succeed or was aborted).
  onRetry?: () => void;
}

// Renders one turn event per its payload kind. Events whose kind this client
// predates (all payloads null) fall back to a plain chip with fallbackText.
export const OpusAgentEventBlock: React.FC<OpusAgentEventBlockProps> = ({
  event,
  turnStatus,
  onRetry,
}) => {
  const kind = getEventKind(event.payload);

  switch (kind) {
    case "assistantText":
      return (
        <div className="break-words">
          {renderMarkdown(event.payload.assistantText!.text, { dense: true })}
        </div>
      );
    case "thinking":
      return <ThinkingBlock text={event.payload.thinking!.text} />;
    case "toolCall": {
      const status = resolveToolCallStatus(event, turnStatus);
      const { toolName, label, detail } = event.payload.toolCall!;
      // `name:detail` when the relay says what the call is about (report
      // name, path, pattern, the GraphQL operation).
      const row = toolRowLabel(toolName || label, detail);
      return (
        <ActivityRow
          icon={
            status === "RUNNING" ? (
              <Loader2 className="size-3 shrink-0 animate-spin text-muted-foreground" />
            ) : status === "FAILED" ? (
              <X className="size-3 shrink-0 text-red-7" />
            ) : (
              <Check className="size-3 shrink-0 text-muted-foreground" />
            )
          }
          mono
        >
          {row}
        </ActivityRow>
      );
    }
    case "planUpdate": {
      const { done, total, activeStep, items } = event.payload.planUpdate!;
      return (
        <PlanRow
          summary={`${done}/${total}${activeStep ? ` — ${activeStep}` : ""}`}
          items={items ?? []}
        />
      );
    }
    case "error":
      return (
        <OpusAgentErrorBubble
          message={event.payload.error!.message}
          onRetry={onRetry}
        />
      );
    case "contextCompacted":
      // Quiet activity row; the payload's size is deliberately not shown. A
      // sentence wraps at the drawer's minimum width instead of truncating.
      return (
        <ActivityRow
          icon={<Minimize2 className="size-3 shrink-0 text-muted-foreground" />}
          wrap
        >
          Condensed earlier conversation to stay within limits
        </ActivityRow>
      );
    case "unknown":
      if (!event.payload.fallbackText) return null;
      return (
        <ActivityRow
          icon={<Sparkles className="size-3 shrink-0 text-muted-foreground" />}
        >
          {event.payload.fallbackText}
        </ActivityRow>
      );
  }
};

// One compact line per activity — a long tool run reads as an activity log,
// not a stack of pills. Raw tool identifiers render in mono (the span's own
// font class beats the Text variant's inherited sans).
const ActivityRow: React.FC<{
  icon: React.ReactNode;
  mono?: boolean;
  // Prose rows wrap; identifier rows run to the drawer's right edge and
  // ellipsise there, with Text's own tooltip carrying the full value.
  wrap?: boolean;
  children: React.ReactNode;
}> = ({ icon, mono, wrap, children }) => (
  <div
    data-testid="opus-agent-activity-row"
    className={cn(
      "flex min-w-0 gap-1.5",
      wrap ? "items-start [&>svg]:mt-0.5" : "items-center",
    )}
  >
    {icon}
    <Text
      variant="fine-print-title"
      multiline={wrap}
      ellipsis={!wrap}
      ellipsisParentFlexDirection="row"
      // The truncated text IS the row, so the full value should not wait.
      ellipsisTooltipDelayMs={0}
      className={cn("min-w-0 text-muted-foreground", mono && "font-mono")}
    >
      {children}
    </Text>
  </div>
);

// The plan row: "done/total — active step" as today, expandable into the
// items when the payload carries them (collapsed by default, same
// collapsible pattern as the thinking block). Without items it is a plain
// row — no chevron.
const PlanRow: React.FC<{
  summary: string;
  items: { text: string; status: string }[];
}> = ({ summary, items }) => {
  const [open, setOpen] = useState(false);
  const icon = <ListChecks className="size-3 shrink-0 text-muted-foreground" />;
  if (items.length === 0)
    return <ActivityRow icon={icon}>{summary}</ActivityRow>;
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      data-testid="opus-agent-activity-row"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid="opus-agent-plan-toggle"
          className="flex w-full min-w-0 items-center gap-1.5 text-left"
        >
          {icon}
          <Text
            variant="fine-print-title"
            className="min-w-0 truncate text-muted-foreground"
          >
            {summary}
          </Text>
          <ChevronRight
            className={cn(
              "size-3 shrink-0 text-muted-foreground transition-transform duration-200",
              open && "rotate-90",
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul
          data-testid="opus-agent-plan-items"
          className="mt-1 flex flex-col gap-1 pl-[18px]"
        >
          {items.map((item, index) => {
            const status = planItemStatus(item.status);
            return (
              <li
                key={`${index}-${item.text}`}
                data-testid="opus-agent-plan-item"
                data-status={status}
                className="flex min-w-0 items-start gap-1.5"
              >
                {status === "completed" ? (
                  <Check
                    aria-label="Completed"
                    className="mt-0.5 size-3 shrink-0 text-muted-foreground"
                  />
                ) : status === "in_progress" ? (
                  <Loader2
                    aria-label="In progress"
                    className="mt-0.5 size-3 shrink-0 animate-spin text-muted-foreground"
                  />
                ) : (
                  <Circle
                    aria-label="Pending"
                    className="mt-0.5 size-3 shrink-0 text-muted-foreground"
                  />
                )}
                <Text
                  variant="fine-print-title"
                  multiline
                  className={cn(
                    "min-w-0",
                    status === "completed"
                      ? "text-muted-foreground"
                      : "text-text-primary",
                  )}
                >
                  {item.text}
                </Text>
              </li>
            );
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Ask Opus never surfaces model reasoning, so there is no design to mirror;
// keep thinking collapsed and muted.
const ThinkingBlock: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 text-muted-foreground"
        >
          <ChevronRight
            className={cn(
              "size-3 shrink-0 transition-transform duration-200",
              open && "rotate-90",
            )}
          />
          <Text variant="fine-print-title" className="text-muted-foreground">
            Thinking
          </Text>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Text
          variant="fine-print-description"
          multiline
          className="mt-1 block border-l-2 border-gray-2 pl-2 text-muted-foreground"
        >
          {text}
        </Text>
      </CollapsibleContent>
    </Collapsible>
  );
};
